import type TableView from "../store/schema/TableView";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

/**
 * Resolves a collapsed-group-header's headerText to the rowKeys hidden under
 * it. Returns undefined when the header is no longer present (e.g. unloaded
 * between selection and action time).
 *
 * Registered by a higher layer (owa-virtualized-group-headers wiring) once it
 * has access to the headers store. Defaults to returning undefined so that the
 * core data layer has zero dependency on the headers store and we avoid a
 * circular package reference.
 */
export type HeaderRowKeysResolver = (headerText: string) => readonly string[] | undefined;

let headerRowKeysResolver: HeaderRowKeysResolver = () => undefined;

/**
 * Registers the header → rowKeys resolver used by `getResolvedSelectedRowKeys`.
 * Wired up at runtime by a package that depends on both this store and the
 * virtualized group headers store; this layer cannot import the headers store
 * directly without creating a circular package dependency.
 */
export function setSelectedHeaderRowKeysResolver(resolver: HeaderRowKeysResolver): void {
    headerRowKeysResolver = resolver;
}

/**
 * Resolves the user's selection in a TableView to a flat list of literal rowKeys.
 *
 * The result is the concatenation of:
 *   - every key in `tableView.selectedRowKeys` (rows the user picked individually), then
 *   - every rowKey listed under each header in `tableView.selectedGroupHeaders`,
 *     looked up via the registered `HeaderRowKeysResolver`.
 *
 * **Invariant (enforced by the selection mutators):** a row is selected either
 * individually (its key lives in `selectedRowKeys`) or through its containing
 * collapsed group header (the header's `headerText` lives in
 * `selectedGroupHeaders`) — never both. The mutators that add a header strip
 * its rows from `selectedRowKeys`, and the mutators that add a row strip its
 * containing header from `selectedGroupHeaders`. Because of this invariant the
 * two contributions are disjoint and no dedup pass is needed here.
 *
 * Headers that resolve to undefined are silently skipped (the header may have
 * unloaded between selection and action time).
 *
 * While the `tri-group-navigation-dev` flight is OFF, `selectedGroupHeaders` is
 * always empty and this function returns the same array as
 * `[...tableView.selectedRowKeys.keys()]`.
 *
 * @param tableView the table view
 * @returns the resolved selection as an array of rowKeys
 */
export default function getResolvedSelectedRowKeys(tableView: TableView): string[] {
    const resolved = [...tableView.selectedRowKeys.keys()];
    
    if (!isGlobalFeatureEnabled("tri-group-navigation-dev") ||
        tableView.selectedGroupHeaders.size === 0) {
        return resolved;
    }
    
    for (const headerText of tableView.selectedGroupHeaders.keys()) {
        const rowKeys = headerRowKeysResolver(headerText);
        if (!rowKeys) {
            continue;
        }
        for (const rowKey of rowKeys) {
            resolved.push(rowKey);
        }
    }
    
    return resolved;
}
